import { GetStaticProps, GetStaticPaths, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '@/pages/_app';
import Head from 'next/head';
import Layout from '@/index-layouts';
import { AppSettings } from '@/shared/app-common';
import {
  getArticleDetailContent,
  getArticleListData,
  getArticleRecommendList,
  getArticleRecommendTags,
  getNewArticleDetailData,
} from '@/lib/service';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { imgUrl } from '@/shared/utils';
import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import { JSDOM } from 'jsdom';
import { blogDetailJsonLd, faqJsonLd } from '@/lib/common/jsonld';
import { removeLastPathSegment } from '@/shared/path-helper';
import { DynamicISRDebugPanel as ISRDebugPanel } from '@/components/ISRDebugPanel/dynamic';
import { ISR_REVALIDATE_TIME } from '@/lib/config/isr';
import { getTdkApi } from '@/lib/service/tdk';
import { canonicalUrl } from '@/utils/utils';

const ArticleDetail = dynamic(() => import('@/components/ArticleDetail'), {
  ssr: true,
});

// ⭐ ISR: 预生成博客详情页路径（只预生成英文文章，其他语言通过 Fallback 生成）
export const getStaticPaths: GetStaticPaths = async () => {
  const paths: { params: { id: string }; locale: string }[] = [];

  // 只预生成英文文章（所有英文文章）
  try {
    const articlesRes = await getArticleListData({ page: 1, size: 9999 }, 'en');
    if (articlesRes.code === 0 && articlesRes.data) {
      articlesRes.data.forEach((article: any) => {
        if (article.title_id) {
          paths.push({
            params: { id: article.title_id },
            locale: 'en',
          });
        }
      });
    }
  } catch {
    // Silently handle error during build
  }

  return {
    paths,
    fallback: 'blocking', // 其他语言和新文章首次访问时生成
  };
};

// ⭐ ISR: 转换为静态生成 + 1 天重新验证
export const getStaticProps: GetStaticProps = async (context) => {
  const { params, locale } = context;
  const resolvedUrl = `/blog/${params.id}`;
  const parentPath = removeLastPathSegment(resolvedUrl);

  const { id } = params;

  // ⭐ ISR 测试：服务端调试日志（删除时搜索 isrGenerated）
  console.log(
    `[ISR] 🔄 Regenerating page: /blog/${id} | Locale: ${locale} | Time: ${new Date().toLocaleString('zh-CN')}`,
  );
  console.log(`[ISR] 🔄 ISR_REVALIDATE_TIME: ${ISR_REVALIDATE_TIME}`);

  const [tdkRes, localeRes, articleDetailRes] = await Promise.all([
    getTdkApi(resolvedUrl.split('?')[0], locale),
    serverSideTranslations(locale, ['common']),
    getNewArticleDetailData(id as string, locale),
  ]);

  if (articleDetailRes.code != 0) {
    return {
      notFound: true,
    };
  }

  let detailContent: any = null;
  if (articleDetailRes?.data?.content_url != undefined) {
    detailContent = await getArticleDetailContent(articleDetailRes?.data?.content_url ?? '');
  } else {
    return {
      notFound: true,
    };
  }

  const dom = new JSDOM(detailContent.data.body);
  const doc = dom.window.document;

  // 移除所有 style 标签
  doc.querySelectorAll('style').forEach((style) => style.remove());

  // 提取 h2 标签文本
  const h2Texts = Array.from(doc.querySelectorAll('h2')).map((h2) => h2.textContent.trim());

  // 处理图片懒加载
  doc.querySelectorAll('img').forEach((img) => {
    if (!img.hasAttribute('loading')) {
      img.setAttribute('loading', 'lazy');
    }
  });

  detailContent.data.body = doc.body.innerHTML;

  let recommendTags: any = null;
  let recommendAllArticles: any = null;
  if (articleDetailRes?.data?.article_id != undefined) {
    [recommendTags, recommendAllArticles] = await Promise.all([
      getArticleRecommendTags({ currentId: `${articleDetailRes?.data?.article_id}` }, locale),
      getArticleRecommendList({ currentId: `${articleDetailRes?.data?.article_id}` }, locale),
    ]);
  }

  let currentURL = `${process.env.NEXT_APP_WEB_HOST}${resolvedUrl}`;
  if (locale != 'en') {
    currentURL = `${process.env.NEXT_APP_WEB_HOST}/${locale}${resolvedUrl}`;
  }
  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};

  const model = articleDetailRes?.data !== undefined ? { ...articleDetailRes?.data } : null;
  const siteUrl = locale !== 'en' ? `${process.env.NEXT_APP_WEB_HOST}/${locale}` : `${process.env.NEXT_APP_WEB_HOST}`;
  const parentUrl = siteUrl + parentPath;
  const pageTitle = model?.title;
  const pageDescription =
    detailContent?.data?.media_name != undefined
      ? tempData['head_Blog_Meta_Desc'].replace('x111x', `${detailContent?.data?.media_name}`)
      : detailContent?.data?.introduction || null;

  const jsonld = blogDetailJsonLd(
    {
      title: pageTitle,
      desc: pageDescription,
      url: currentURL,
    },
    {
      url: parentUrl,
    },
    { url: siteUrl },
  );

  // 生成FAQ JSON-LD（如果满足条件）
  const getDesc = () => {
    if (tdkRes?.data?.description) {
      return tdkRes?.data?.description;
    }
    return detailContent?.data?.media_name != undefined
      ? tempData['head_Blog_Meta_Desc'].replace('x111x', `${detailContent?.data?.media_name}`)
      : detailContent?.data?.introduction || null;
  };
  let faqJsonLdData = null;
  const articleFaq = articleDetailRes?.data?.article_faq;
  if (
    articleFaq &&
    articleFaq.enable === true &&
    articleFaq.faqs &&
    Array.isArray(articleFaq.faqs) &&
    articleFaq.faqs.length > 0
  ) {
    // 过滤掉空的问答对
    const validFaqs = articleFaq.faqs.filter(
      (faq) => faq.question && faq.answer && faq.question.trim() !== '' && faq.answer.trim() !== '',
    );

    if (validFaqs.length > 0) {
      faqJsonLdData = faqJsonLd(
        {
          title: tdkRes?.data?.title || detailContent?.data?.title || null,
          desc: getDesc(),
          url: canonicalUrl(process.env.NEXT_APP_WEB_HOST, resolvedUrl, locale),
        },
        { url: siteUrl },
        validFaqs.map((faq) => ({
          question: faq.question,
          answer: faq.answer,
        })),
      );
    }
  }

  return {
    props: {
      h2Texts: h2Texts,
      ...localeRes,
      model: model,
      contentData: detailContent?.data !== undefined ? { ...detailContent?.data } : null,
      herf: currentURL,
      recommendTags: recommendTags?.data !== undefined ? recommendTags?.data : null,
      recommandArtcles: recommendAllArticles?.data !== undefined ? recommendAllArticles?.data : null,
      description: pageDescription,
      jsonLd: jsonld,
      faqJsonLd: faqJsonLdData,
      // ⭐ ISR 信息
      isrGeneratedAt: new Date().toISOString(),
      isrRevalidateIn: ISR_REVALIDATE_TIME,
    },
    revalidate: ISR_REVALIDATE_TIME,
  };
};

const ArticleDetailPage: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = (props) => {
  const getContent = useMemo(() => <ArticleDetail data={props} />, [props]);

  return (
    <>
      <ISRDebugPanel
        generatedAt={props.isrGeneratedAt}
        revalidateIn={props.isrRevalidateIn}
        pageName="博客详情页"
        additionalInfo={{
          语言: props.model?.locale || 'N/A',
          '文章 ID': props.model?.title_id,
        }}
      />
      {getContent}
    </>
  );
};

ArticleDetailPage.getLayout = function getLayout(page: React.ReactElement) {
  const contentData = page.props?.contentData;
  const modelData = page.props?.model;
  const jsonLd = page.props?.jsonLd;

  const title = contentData?.title;
  const desc = page.props?.description;

  const faqJsonLdData = page.props?.faqJsonLd;

  return (
    <Layout
      head={
        <Head>
          <title>{`${title} | ${AppSettings.name}`}</title>
          {/* <meta name="title" content={`${contentData?.title} | ${AppSettings.name}`} /> */}
          <meta name="description" content={desc} />
          <meta name="keywords" content={`${contentData?.tags || ''}`} />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={imgUrl(contentData?.image_url, 'mid')} />
          <meta property="og:image:secure_url" content={imgUrl(contentData?.image_url, 'mid')} />
          <meta property="og:type" content="article" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={imgUrl(contentData?.image_url, 'mid')} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
          <link rel="icon" href={AppSettings.icoPath} />
          <link rel="canonical" href={page.props?.herf} />
          <link rel="preload" href={imgUrl(contentData?.image_url, 'mid')} as="image" />
          <link
            rel="alternate"
            hrefLang="en"
            href={`${process.env.NEXT_APP_WEB_HOST}/en/blog/${modelData?.title_id}`}
          />
          <link
            rel="alternate"
            hrefLang="tw"
            href={`${process.env.NEXT_APP_WEB_HOST}/tw/blog/${modelData?.title_id}`}
          />
          <link
            rel="alternate"
            hrefLang="zh"
            href={`${process.env.NEXT_APP_WEB_HOST}/zh/blog/${modelData?.title_id}`}
          />
          <link
            rel="alternate"
            hrefLang="ja"
            href={`${process.env.NEXT_APP_WEB_HOST}/ja/blog/${modelData?.title_id}`}
          />
          <link
            rel="alternate"
            hrefLang="ko"
            href={`${process.env.NEXT_APP_WEB_HOST}/ko/blog/${modelData?.title_id}`}
          />
          <link
            rel="alternate"
            hrefLang="pt"
            href={`${process.env.NEXT_APP_WEB_HOST}/pt/blog/${modelData?.title_id}`}
          />
          <link
            rel="alternate"
            hrefLang="de"
            href={`${process.env.NEXT_APP_WEB_HOST}/de/blog/${modelData?.title_id}`}
          />
          <link
            rel="alternate"
            hrefLang="es"
            href={`${process.env.NEXT_APP_WEB_HOST}/es/blog/${modelData?.title_id}`}
          />

          <link
            rel="alternate"
            hrefLang="it"
            href={`${process.env.NEXT_APP_WEB_HOST}/it/blog/${modelData?.title_id}`}
          />
          <link
            rel="alternate"
            hrefLang="x-default"
            href={`${process.env.NEXT_APP_WEB_HOST}/blog/${modelData?.title_id}`}
          />
          {jsonLd && (
            <script async type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
          )}

          {/* FAQ的JSON-LD（如果存在） */}
          {faqJsonLdData && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLdData) }}
            />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default ArticleDetailPage;
